import { MinusIcon, PlusIcon } from "@heroicons/react/16/solid";
import { useStore } from "@nanostores/react";
import { NodeCollection, NodeSingular } from "cytoscape";
import { FC, ReactElement, useCallback } from "react";
import { toast } from "react-toastify";

import { GraphLayout, GraphSummary } from "../api";
import IconArrowsMove from "../assets/icons/arrows-move.svg";
import IconBinaryTree from "../assets/icons/binary-tree.svg";
import IconBinaryTreeCircle from "../assets/icons/binary-tree-circle.svg";
import { hasBeenManuallyPositioned } from "../services/graph/apply-layout-format.ts";
import { updateNodePositions } from "../services/graph/update-graph.ts";
import {
  $graphLayoutFormat,
  updateGraphLayoutFormat,
} from "../stores/graph/content.ts";
import { $cytoscapeInstance, $expandCollapseApi } from "../stores/graph/ui.ts";
import { $router } from "../stores/router.ts";
import { Button } from "./catalyst/Button.tsx";
import { Tooltip } from "./Tooltip.tsx";

type Props = {
  isDisabled: boolean;
};

export const GraphLayoutsControls: FC<Props> = ({
  isDisabled,
}): ReactElement => {
  const cytoscapeInstance = useStore($cytoscapeInstance);
  const expandCollapseApi = useStore($expandCollapseApi);
  const router = useStore($router);
  const graphId = router?.route === "graph" ? router.params.id : null;
  const currentFormat = useStore($graphLayoutFormat);

  // Helper function to get node positions efficiently
  const getNodePositions = useCallback((nodes: NodeCollection) => {
    return nodes.map((node) => ({
      nodeId: node.id(),
      position: node.position(),
    }));
  }, []);

  const handleFreeMoveLayout = useCallback(async (): Promise<void> => {
    const cytoscapeInstance = $cytoscapeInstance.get();
    if (!cytoscapeInstance || !graphId) return;

    const nodes = cytoscapeInstance.nodes();

    // Get current positions of all nodes to freeze them in place
    const layoutPositions = nodes.map((node: NodeSingular) => {
      const currentPosition = node.position();
      return {
        nodeId: node.id(),
        position: { x: currentPosition.x, y: currentPosition.y },
      };
    });

    // Save current positions to server
    if (layoutPositions.length > 0) {
      // Also update originalPosition locally so they're considered manually positioned
      layoutPositions.forEach(({ nodeId, position }) => {
        cytoscapeInstance
          .getElementById(nodeId)
          .data("originalPosition", position);
      });

      void updateNodePositions(graphId, layoutPositions);
    }

    // Save format to backend (gracefully handles errors)
    void updateGraphLayoutFormat(graphId, GraphLayout.MANUAL);
  }, [graphId]);

  // Unified handler for layout format changes
  const handleFormatChange = useCallback(
    async (format: GraphSummary["format"]): Promise<void> => {
      if (!cytoscapeInstance || !graphId) return;

      // Special handling for manual layout
      if (format === GraphLayout.MANUAL) {
        await handleFreeMoveLayout();
        return;
      }

      // Clear any manually positioned nodes when applying a new layout
      const nodePositions = getNodePositions(cytoscapeInstance.nodes());
      const nodesToClear = nodePositions.filter(({ nodeId }) =>
        hasBeenManuallyPositioned(cytoscapeInstance.getElementById(nodeId)),
      );

      if (nodesToClear.length > 0) {
        // Clear originalPosition locally so nodes aren't considered manually positioned
        nodesToClear.forEach(({ nodeId }) => {
          cytoscapeInstance
            .getElementById(nodeId)
            .data("originalPosition", { x: 0, y: 0 });
        });

        // Send zero positions to server to clear saved positions
        void updateNodePositions(
          graphId,
          nodesToClear.map(({ nodeId }) => ({
            nodeId,
            position: { x: 0, y: 0 },
          })),
        );
      }

      // Save format to backend (gracefully handles errors)
      void updateGraphLayoutFormat(graphId, format);
    },
    [cytoscapeInstance, graphId, getNodePositions, handleFreeMoveLayout],
  );

  const handleFcoseLayoutClick = useCallback((): void => {
    handleFormatChange(GraphLayout.FCOSE);
  }, [handleFormatChange]);

  const handleHorizontalTreeLayoutClick = useCallback((): void => {
    handleFormatChange(GraphLayout.HORIZONTALTREE);
  }, [handleFormatChange]);

  const handleVerticalTreeLayoutClick = useCallback((): void => {
    handleFormatChange(GraphLayout.VERTICALTREE);
  }, [handleFormatChange]);

  const handleCircleLayoutClick = useCallback((): void => {
    handleFormatChange(GraphLayout.CIRCLE);
  }, [handleFormatChange]);

  const handleManualLayoutClick = useCallback((): void => {
    handleFormatChange(GraphLayout.MANUAL);
  }, [handleFormatChange]);

  const handleExpandAllClick = useCallback((): void => {
    if (!expandCollapseApi) {
      console.warn("Expand-collapse API not available");
      return;
    }

    try {
      expandCollapseApi.expandAll();
    } catch (error) {
      console.error("Failed to expand all groups:", error);
      toast.error("Failed to expand all groups");
    }
  }, [expandCollapseApi]);

  const handleCollapseAllClick = useCallback((): void => {
    if (!expandCollapseApi) {
      console.warn("Expand-collapse API not available");
      return;
    }

    try {
      expandCollapseApi.collapseAll();
    } catch (error) {
      console.error("Failed to collapse all groups:", error);
      toast.error("Failed to collapse all groups");
    }
  }, [expandCollapseApi]);

  return (
    <div
      aria-label="Graph layout controls"
      className="ml-auto flex"
      role="toolbar"
    >
      <Tooltip content="Expand all groups">
        <Button
          aria-label="Expand all groups"
          className="opacity-50"
          disabled={isDisabled}
          onClick={handleExpandAllClick}
          plain
          size="xs"
        >
          <PlusIcon aria-hidden="true" className="h-5 w-5 text-zinc-500" />
        </Button>
      </Tooltip>

      <Tooltip content="Collapse all groups">
        <Button
          aria-label="Collapse all groups"
          className="opacity-50"
          disabled={isDisabled}
          onClick={handleCollapseAllClick}
          plain
          size="xs"
        >
          <MinusIcon aria-hidden="true" className="h-5 w-5 text-zinc-500" />
        </Button>
      </Tooltip>

      <Tooltip content="Free move layout">
        <Button
          aria-label="Free move layout"
          className={currentFormat === GraphLayout.MANUAL ? "" : "opacity-50"}
          disabled={isDisabled}
          onClick={handleManualLayoutClick}
          plain
          size="xs"
        >
          <IconArrowsMove
            aria-hidden="true"
            className={`h-5 w-5 ${currentFormat === GraphLayout.MANUAL ? "text-zinc-200" : "text-zinc-500"}`}
          />
        </Button>
      </Tooltip>

      <Tooltip content="Force-directed layout (optimal for clusters)">
        <Button
          aria-label="Force-directed layout (optimal for clusters)"
          className={currentFormat === GraphLayout.FCOSE ? "" : "opacity-50"}
          disabled={isDisabled}
          onClick={handleFcoseLayoutClick}
          plain
          size="xs"
        >
          <IconBinaryTreeCircle
            aria-hidden="true"
            className={`h-5 w-5 ${currentFormat === GraphLayout.FCOSE ? "text-zinc-200" : "text-zinc-500"}`}
          />
        </Button>
      </Tooltip>

      <Tooltip content="Horizontal tree layout">
        <Button
          aria-label="Tree layout"
          className={
            currentFormat === GraphLayout.HORIZONTALTREE ? "" : "opacity-50"
          }
          disabled={isDisabled}
          onClick={handleHorizontalTreeLayoutClick}
          plain
          size="xs"
        >
          <IconBinaryTree
            aria-hidden="true"
            className={`h-5 w-5 -rotate-90 ${currentFormat === GraphLayout.HORIZONTALTREE ? "text-zinc-200" : "text-zinc-500"}`}
          />
        </Button>
      </Tooltip>

      <Tooltip content="Vertical tree layout">
        <Button
          aria-label="Tree layout"
          className={
            currentFormat === GraphLayout.VERTICALTREE ? "" : "opacity-50"
          }
          disabled={isDisabled}
          onClick={handleVerticalTreeLayoutClick}
          plain
          size="xs"
        >
          <IconBinaryTree
            aria-hidden="true"
            className={`h-5 w-5 ${currentFormat === GraphLayout.VERTICALTREE ? "text-zinc-200" : "text-zinc-500"}`}
          />
        </Button>
      </Tooltip>

      <Tooltip content="Circle layout">
        <Button
          aria-label="Circle layout"
          className={currentFormat === GraphLayout.CIRCLE ? "" : "opacity-50"}
          disabled={isDisabled}
          onClick={handleCircleLayoutClick}
          plain
          size="xs"
        >
          <IconBinaryTreeCircle
            aria-hidden="true"
            className={`h-5 w-5 ${currentFormat === GraphLayout.CIRCLE ? "text-zinc-200" : "text-zinc-500"}`}
          />
        </Button>
      </Tooltip>
    </div>
  );
};
